import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 * @param { Array.<Product> } productsData
 * @returns { Array.<CartItem> }
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  if (!cartData || !productsData) return [];

  return cartData
    .map((item) => {
      const product = productsData.find((p) => p._id === item.productId);
      return product ? { ...product, ...item } : null;
    })
    .filter(Boolean);
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 * @returns { Number }
 */
export const getTotalCartValue = (items = []) => {
  return items.reduce((total, item) => total + item.cost * item.qty, 0);
};

/**
 * Return the sum of quantities of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 * @returns { Number }
 */
export const getTotalItems = (items = []) => {
  return items.reduce((total, item) => total + item.qty, 0);
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 * @param {Function} handleAdd
 * @param {Function} handleDelete
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 */
const ItemQuantity = ({ value, handleAdd, handleDelete, isReadOnly }) => {
  if (isReadOnly) {
    return <Box padding="0.5rem">Qty: {value}</Box>;
  }

  return (
    <Stack direction="row" alignItems="center">
      <IconButton size="small" color="primary" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 * @param { Array.<CartItem> } items
 * @param {Function} handleQuantity
 *    Called with (productId, qty) to update the quantity of a product in cart
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 */
const Cart = ({ products, items = [], handleQuantity, isReadOnly = false }) => {
  const history = useHistory();

  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className="cart">
        {items
          .filter((item) => item.qty > 0)
          .map((item) => (
            <Box
              display="flex"
              alignItems="flex-start"
              padding="1rem"
              key={item.productId}
            >
              <Box className="image-container">
                <img src={item.image} alt={item.name} width="100%" height="100%" />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="6rem"
                paddingX="1rem"
              >
                <div>{item.name}</div>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <ItemQuantity
                    value={item.qty}
                    isReadOnly={isReadOnly}
                    handleAdd={() => handleQuantity(item.productId, item.qty + 1)}
                    handleDelete={() =>
                      handleQuantity(item.productId, item.qty - 1)
                    }
                  />
                  <Box padding="0.5rem" fontWeight="700">
                    ${item.cost}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>

        {!isReadOnly && (
          <Box display="flex" justifyContent="flex-end" className="cart-footer">
            <Button
              color="primary"
              variant="contained"
              startIcon={<ShoppingCart />}
              className="checkout-btn"
              onClick={() => history.push("/checkout")}
            >
              Checkout
            </Button>
          </Box>
        )}
      </Box>

      {isReadOnly && (
        <Box className="cart" padding="1rem">
          <h2>Order Details</h2>
          <Box display="flex" justifyContent="space-between" paddingY="0.25rem">
            <span>Products</span>
            <span>{getTotalItems(items)}</span>
          </Box>
          <Box display="flex" justifyContent="space-between" paddingY="0.25rem">
            <span>Subtotal</span>
            <span>${getTotalCartValue(items)}</span>
          </Box>
          <Box display="flex" justifyContent="space-between" paddingY="0.25rem">
            <span>Shipping Charges</span>
            <span>$0</span>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            paddingY="0.25rem"
            fontWeight="700"
          >
            <span>Total</span>
            <span>${getTotalCartValue(items)}</span>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Cart;
